// The Take: the stage as the director's camera sees it, plus Lia's voice, into one mp4 Blob
// via the browser's own MediaRecorder. The studio previews the Blob and posts it. Ported
// from office-news record.ts.
import { voiceStream } from "./voice";

let tape = null;

export const isRecording = () => tape !== null;

// H.264 High profile first: same picture as Baseline at roughly a third less bitrate, and
// mp4/AAC is what everything downstream (QuickTime, Premiere, WhatsApp) opens without asking.
// mp4 only, on purpose: an Episode is promised as mp4, so a browser without an mp4 muxer
// (Firefox today) does not tape at all — the studio says so instead of shipping a WebM.
const MIME = [
    "video/mp4;codecs=avc1.640028,mp4a.40.2",   // High@4.0
    "video/mp4;codecs=avc1.4D4028,mp4a.40.2",   // Main@4.0
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",   // Baseline
    "video/mp4",
];

export const canTapeMp4 = () =>
    typeof MediaRecorder !== "undefined" && MIME.some((m) => MediaRecorder.isTypeSupported(m));

export const AUDIO_BPS = 96_000;
// The server rejects Episodes over 70 MB; 62 MB of payload leaves room for container overhead
// and MediaRecorder overshooting its target.
const BUDGET_BITS = 62e6 * 8;
// 1.6 Mbps is plenty for 720p30 of a flat-shaded, low-motion set — the ceiling only drops
// from here, and only for takes long enough to threaten the cap (past ~5 minutes).
const CEILING_BPS = 1_600_000;

/** Video bitrate that keeps a take of `seconds` inside the budget, within sane bounds.
 * ponytail: the floor wins on very long takes — the studio warns instead of degrading further. */
export const budgetVideoBps = (seconds) =>
    Math.round(Math.min(CEILING_BPS, Math.max(600_000, BUDGET_BITS / Math.max(30, seconds) - AUDIO_BPS)));

/** `seconds` is the expected length of the take (see the studio's voice-duration sum). */
export function startRecording(canvas, seconds = 60) {
    if (tape) return true;
    if (typeof MediaRecorder === "undefined" || !canvas.captureStream) return false;
    const stream = canvas.captureStream(30);
    for (const track of voiceStream().getAudioTracks()) stream.addTrack(track);
    const mimeType = MIME.find((m) => MediaRecorder.isTypeSupported(m));
    if (!mimeType) return false;
    const rec = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: budgetVideoBps(seconds), audioBitsPerSecond: AUDIO_BPS });
    const t = { rec, chunks: [], startedAt: Date.now() };
    rec.ondataavailable = (e) => { if (e.data.size) t.chunks.push(e.data); };
    rec.start(1_000);
    tape = t;
    return true;
}

/** Resolves with the Blob and its length in seconds. */
export function stopRecording() {
    const t = tape;
    tape = null;
    if (!t) return Promise.resolve(null);
    return new Promise((resolve) => {
        t.rec.onstop = () => resolve({
            blob: new Blob(t.chunks, { type: t.rec.mimeType || "video/mp4" }),
            seconds: Math.round((Date.now() - t.startedAt) / 1000),
        });
        t.rec.stop();
    });
}
